import React, { useCallback, useMemo, useRef, useState } from "react";
import { UnitFormatsProvider } from "@strava/units/react";
import { UNIT_METRIC } from "@strava/units";
import { Map, MapBounds, type LatLng } from "@strava/feature-global-wasm-mre";

import I18n from "../../utils/I18n";
import StravaPackageContextProviders from "../../apps/StravaPackageContextProviders";

import BeaconMapContent from "./BeaconMapContent";
import { isFinished } from "./blipClassName";
import useBeaconPolling from "./useBeaconPolling";
import garminLogo from "./assets/garmin-live-track-map-logo.png";
import styles from "./BeaconMap.scss";

// Mapbox-style zoom level (0–22) used for the initial flyToWithZoomLevel on
// a live Beacon view.
//
// Live Beacons render <BeaconMapContent>, which calls camera.flyToWithZoomLevel
// directly (in flyCameraTo) instead of using `bounds` prop. The `bounds` prop
// hardcodes GENEROUS_CAMERA_MARGINS (10% each side), but our subsequent
// translateTo follow-updates use FOLLOW_MARGINS (zeros). Mismatched margins
// cause MRE to recompute camera distance on the first follow-update,
// producing a visible zoom shift. Calling flyToWithZoomLevel ourselves with
// FOLLOW_MARGINS keeps the margins consistent across init and follow.
//
// 17 is one level wider than the legacy Mapbox/Leaflet view's default (18) —
// the MRE rendering at 18 felt slightly too close to the ground for live
// tracking.
const INITIAL_FLY_ZOOM_LEVEL = 17;

const GARMIN_SOURCE_APP = 'Garmin Connect';

interface BeaconMapProps {
  beaconId: string;
  initialPolyline: LatLng[];
  initialUpdateTime?: number;
  status: number;
  sourceApp?: string;
  pollingInterval: number;
}

const BeaconMap = ({
  beaconId,
  initialPolyline,
  initialUpdateTime,
  status: initialStatus,
  sourceApp,
  pollingInterval,
}: BeaconMapProps) => {
  const isFromGarmin = sourceApp === GARMIN_SOURCE_APP;
  const { livePolyline, status } = useBeaconPolling({
    beaconId,
    initialPolyline,
    initialStatus,
    initialUpdateTime,
    pollingInterval,
  });

  const [autoCenter, setAutoCenter] = useState(true);
  const [recenterTrigger, setRecenterTrigger] = useState(0);

  // Initial-render snapshot of whether this Beacon is a finished activity.
  // Drives which camera-init strategy to use; captured at mount so a status
  // transition mid-session (live → finished) doesn't tear down the live
  // camera setup.
  const wasInitiallyFinished = useRef(isFinished(initialStatus));

  // Bounds to pass to Map when the activity was already finished at
  // page load: full route, fitted via flyToBounds. For live activities we
  // pass `null` as BeaconMapContent owns the initial camera placement 
  // (see flyCameraTo in cameraHelpers).
  const finishedBounds = useRef<MapBounds | null>(
    isFinished(initialStatus) ? { points: initialPolyline } : null
  );

  const currentPosition: LatLng | null = useMemo(
    () => (livePolyline.length > 0 ? livePolyline[livePolyline.length - 1] : null),
    [livePolyline]
  );

  const handleUserInteraction = useCallback(() => {
    setAutoCenter(false);
  }, []);

  const handleRecenter = useCallback(() => {
    setAutoCenter(true);
    setRecenterTrigger((n) => n + 1);
  }, []);

  // Don't render the map at all until we have something to show. The
  // beacon page may be opened before the activity actually starts, in
  // which case `livePolyline` is empty (status NOT_STARTED). useBeaconPolling
  // stays active above this guard, so as soon as the first point arrives we
  // re-render and the map appears with the start marker and live blip.
  // BeaconMapContent handles the < 2 polyline case itself by skipping
  // <Map.Route> (MRE's displayRoute throws on shorter polylines).
  if (livePolyline.length < 1) {
    return null;
  }

  return (
    <StravaPackageContextProviders>
      <UnitFormatsProvider
        locale={I18n.language()}
        unitSystem={UNIT_METRIC}
      >
        <div className={styles.mapContainer}>
          <Map
            page={process.env.BEACON_ANALYTICS_PAGE}
            category={process.env.BEACON_ANALYTICS_CATEGORY}
            surfaceIdentifier={process.env.BEACON_SURFACE_IDENTIFIER}
            enable3D={false}
            bounds={finishedBounds.current}
          >
            <Map.UIControls
              topRightOverride={null}
              bottomLeftOverride={null}
              bottomRightOverride={null}
            />
            <BeaconMapContent
              beaconId={beaconId}
              livePolyline={livePolyline}
              currentPosition={currentPosition}
              status={status}
              autoCenter={autoCenter}
              onUserInteraction={handleUserInteraction}
              recenterTrigger={recenterTrigger}
              initialFlyZoomLevel={wasInitiallyFinished.current ? null : INITIAL_FLY_ZOOM_LEVEL}
            />
          </Map>

          {isFromGarmin && (
            <div className={styles.garminLogo}>
              <img
                src={garminLogo}
                alt="Garmin Live Track"
                className={styles.garminLogoImg}
              />
            </div>
          )}

          {!isFinished(status) && currentPosition && (
            <button
              type="button"
              className={styles.recenterBtn}
              onClick={handleRecenter}
              aria-label={I18n.t("strava.beacon.beacon_map.recenter_aria_label")}
              title={I18n.t("strava.beacon.beacon_map.recenter_title")}
            >
              <svg
                className={styles.recenterIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
              >
                <g fill="#FC5200" fillRule="evenodd">
                  <path d="M16.5 11c0-3.038-2.462-5.5-5.5-5.5S5.5 7.962 5.5 11s2.462 5.5 5.5 5.5 5.5-2.462 5.5-5.5zm-10 0c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5-2.015 4.5-4.5 4.5-4.5-2.015-4.5-4.5zm5-10V.5h-1v6h1V1zm0 15v-.5h-1v6h1V16zm9.5-4.5h.5v-1h-6v1H21zm-15 0h.5v-1h-6v1H6z" />
                  <path d="M11 14c1.736 0 3-1.305 3-3s-1.264-3-3-3-3 1.305-3 3 1.264 3 3 3z" />
                </g>
              </svg>
            </button>
          )}
        </div>
      </UnitFormatsProvider>
    </StravaPackageContextProviders>
  );
};

export default BeaconMap;
